import { useState, useEffect, useCallback, use } from 'react';
import { getFirestore, collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { DBCollection } from '@/lib/utils/constants-shared';
import { AuthContext } from '@/lib/context';
import { ensureUser } from '@/lib/utils/utils';
import useUser from './use-user';
import engagementMethodsData from '@/data/scripture-engagement-methods.json';
import { Analytics } from '@/lib/analytics';

/**
 * Calculate the total number of sub-steps for a given step.
 * Sub-steps can be: questions, capabilityItems, or method steps.
 */
function getSubStepCount(step: GuidedStudyStep): number {
  // Count questions
  if (step.questions && step.questions.length > 0) {
    return step.questions.length;
  }

  // Count capability items
  if (step.capabilityItems && step.capabilityItems.length > 0) {
    return step.capabilityItems.length;
  }

  // Count internal method steps
  if (step.steps?.length > 0) {
    return step.steps.length;
  }

  // Fallback: Check if there's a methodRef that has steps in our master data
  if (step.methodRef) {
    for (const category of engagementMethodsData.categories) {
      const method = category.methods.find((m: any) => m.id === step.methodRef);
      if (method && method.steps && method.steps.length > 0) {
        return method.steps.length;
      }
    }
  }

  // If no sub-steps, treat the whole step as a single sub-step
  return 1;
}

/**
 * Hook to manage guided study progress for a specific passage and persona.
 * Loads progress from Firestore and provides methods to navigate through steps and sub-steps.
 */
export function useGuidedStudyProgress(passageId: string, personaId: string, guidedStudy: GuidedStudy | undefined) {
  const auth = use(AuthContext);
  const { user, isUserLoading } = useUser();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [currentSubStepIndex, setCurrentSubStepIndex] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [progressDocId, setProgressDocId] = useState<string | null>(null);
  const [textResponses, setTextResponses] = useState<{ [stepIndex: string]: { [subStepIndex: string]: string } }>({});
  const [completedRequirements, setCompletedRequirements] = useState<{ [stepIndex: string]: { [subStepIndex: string]: boolean } }>({});

  // Track previous IDs to reset loading state during render (prevents flicker)
  const [prevPassageId, setPrevPassageId] = useState(passageId);
  const [prevPersonaId, setPrevPersonaId] = useState(personaId);

  if (passageId !== prevPassageId || personaId !== prevPersonaId) {
    setPrevPassageId(passageId);
    setPrevPersonaId(personaId);
    setIsLoading(true);
  }

  const [maxReachedStepIndex, setMaxReachedStepIndex] = useState(0);
  const [maxReachedSubStepIndex, setMaxReachedSubStepIndex] = useState(0);
  const [sessionStartTime] = useState(Date.now());

  const totalSteps = guidedStudy?.steps.length || 0;
  const currentStep = guidedStudy?.steps[currentStepIndex];
  const totalSubSteps = currentStep ? getSubStepCount(currentStep) : 1;

  // Update max reached progress (LOCAL ONLY)
  useEffect(() => {
    if (currentStepIndex > maxReachedStepIndex) {
      setMaxReachedStepIndex(currentStepIndex);
      setMaxReachedSubStepIndex(currentSubStepIndex);
    } else if (currentStepIndex === maxReachedStepIndex && currentSubStepIndex > maxReachedSubStepIndex) {
      setMaxReachedSubStepIndex(currentSubStepIndex);
    }
  }, [currentStepIndex, currentSubStepIndex, maxReachedStepIndex, maxReachedSubStepIndex]);

  // Load progress from Firestore
  useEffect(() => {
    // Wait for auth to resolve
    if (isUserLoading) {
      return;
    }

    if (!user || !passageId || !personaId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const loadProgress = async () => {
      try {
        const db = getFirestore(getApp());
        const q = query(
          collection(db, 'users', user.uid, DBCollection.guidedStudyProgress),
          where('passageId', '==', passageId),
          where('personaId', '==', personaId)
        );

        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const docSnap = snapshot.docs[0];
          const data = docSnap.data() as FirebaseGuidedStudyProgress<Timestamp>;
          setProgressDocId(docSnap.id);
          setCurrentStepIndex(data.currentStepIndex || 0);
          setCurrentSubStepIndex(data.currentSubStepIndex || 0);
          setMaxReachedStepIndex(data.maxStepIndex !== undefined ? data.maxStepIndex : data.currentStepIndex);
          setMaxReachedSubStepIndex(data.maxSubStepIndex !== undefined ? data.maxSubStepIndex : (data.currentSubStepIndex || 0));
          setIsCompleted(data.completed || false);
          setTextResponses(data.textResponses || {});
          setCompletedRequirements(data.completedRequirements || {});
        } else {
          // No existing progress, set initials
          setProgressDocId(null);
          setCurrentStepIndex(0);
          setCurrentSubStepIndex(0);
          setMaxReachedStepIndex(0);
          setMaxReachedSubStepIndex(0);
          setIsCompleted(false);
          setTextResponses({});
          setCompletedRequirements({});
        }
      } catch (error) {
        console.error('Error loading guided study progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [user, passageId, personaId, isUserLoading]);

  // Save progress to Firestore
  const saveProgress = useCallback(async (stepIndex: number, subStepIndex: number) => {
    // Optimistic update: update UI immediately (works for guests too)
    const newMaxStepIndex = Math.max(stepIndex, maxReachedStepIndex);
    const newMaxSubStepIndex = stepIndex > maxReachedStepIndex ? subStepIndex : Math.max(subStepIndex, maxReachedSubStepIndex);

    setCurrentStepIndex(stepIndex);
    setCurrentSubStepIndex(subStepIndex);
    setMaxReachedStepIndex(newMaxStepIndex);
    setMaxReachedSubStepIndex(newMaxSubStepIndex);

    if (!passageId || !personaId || !auth) {
      return;
    }

    // Then save to Firestore in the background
    try {
      const activeUser = await ensureUser(auth);
      const db = getFirestore(getApp());
      const now = Timestamp.now();
      const completed = stepIndex >= totalSteps - 1;

      if (progressDocId) {
        // Update existing progress
        const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
        await updateDoc(progressRef, {
          currentStepIndex: stepIndex,
          currentSubStepIndex: subStepIndex,
          maxStepIndex: newMaxStepIndex,
          maxSubStepIndex: newMaxSubStepIndex,
          totalSteps,
          completed,
          dateUpdated: now
        });
      } else {
        // Create new progress document
        const progressRef = collection(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress);
        const newDoc = await addDoc(progressRef, {
          userId: activeUser.uid,
          passageId,
          personaId,
          currentStepIndex: stepIndex,
          currentSubStepIndex: subStepIndex,
          maxStepIndex: newMaxStepIndex,
          maxSubStepIndex: newMaxSubStepIndex,
          totalSteps,
          completed,
          dateCreated: now,
          dateUpdated: now
        });
        setProgressDocId(newDoc.id);
        Analytics.guidedStudyStart(passageId, personaId);
      }
    } catch (error) {
      console.error('Failed to save guided study progress:', error);
    }
  }, [auth, passageId, personaId, totalSteps, progressDocId, maxReachedStepIndex, maxReachedSubStepIndex]);

  // Jump to a specific step/substep (only if already reached or completed)
  const jumpTo = useCallback(async (stepIndex: number, subStepIndex: number) => {
    const isReached = isCompleted ||
      stepIndex < maxReachedStepIndex ||
      (stepIndex === maxReachedStepIndex && subStepIndex <= maxReachedSubStepIndex);

    if (isReached) {
      // Navigate locally (works for guests too)
      setCurrentStepIndex(stepIndex);
      setCurrentSubStepIndex(subStepIndex);

      // Persist the current position in DB (but don't change max)
      if (auth && passageId && personaId && progressDocId) {
        try {
          const activeUser = await ensureUser(auth);
          const db = getFirestore(getApp());
          const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
          await updateDoc(progressRef, {
            currentStepIndex: stepIndex,
            currentSubStepIndex: subStepIndex,
            dateUpdated: Timestamp.now()
          });
        } catch (error) {
          console.error('Failed to save jump position:', error);
        }
      }
    }
  }, [maxReachedStepIndex, maxReachedSubStepIndex, isCompleted, auth, passageId, personaId, progressDocId]);

  // Navigate to next sub-step or step
  const goToNext = useCallback(() => {
    if (currentSubStepIndex < totalSubSteps - 1) {
      // Move to next sub-step within current step
      saveProgress(currentStepIndex, currentSubStepIndex + 1);
    } else if (currentStepIndex < totalSteps - 1) {
      // Move to next step, reset sub-step to 0
      Analytics.guidedStudyStepComplete(passageId, personaId, currentStepIndex);
      saveProgress(currentStepIndex + 1, 0);
    }
    // If at last sub-step of last step, do nothing (use Complete button instead)
  }, [currentStepIndex, currentSubStepIndex, totalSteps, totalSubSteps, saveProgress]);

  // Navigate to previous sub-step or step
  const goToPrevious = useCallback(() => {
    if (currentSubStepIndex > 0) {
      // Move to previous sub-step within current step
      jumpTo(currentStepIndex, currentSubStepIndex - 1);
    } else if (currentStepIndex > 0) {
      // Move to previous step, go to its last sub-step
      const prevStep = guidedStudy?.steps[currentStepIndex - 1];
      const prevStepSubSteps = prevStep ? getSubStepCount(prevStep) : 1;
      jumpTo(currentStepIndex - 1, prevStepSubSteps - 1);
    }
  }, [currentStepIndex, currentSubStepIndex, guidedStudy, jumpTo]);

  // Reset progress to beginning
  const resetProgress = useCallback(async () => {
    // Reset local state (works for guests too)
    setCurrentStepIndex(0);
    setCurrentSubStepIndex(0);
    setMaxReachedStepIndex(0);
    setMaxReachedSubStepIndex(0);
    setIsCompleted(false);
    setTextResponses({});

    if (!auth || !passageId || !personaId) return;

    try {
      const activeUser = await ensureUser(auth);
      const db = getFirestore(getApp());
      const now = Timestamp.now();

      if (progressDocId) {
        // Update existing progress to reset
        const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
        await updateDoc(progressRef, {
          currentStepIndex: 0,
          currentSubStepIndex: 0,
          maxStepIndex: 0,
          maxSubStepIndex: 0,
          completed: false,
          textResponses: {}, // Clear all text responses
          completedRequirements: {}, // Clear all completed requirements
          dateUpdated: now
        });
      }
    } catch (error) {
      console.error('Failed to reset guided study progress:', error);
    }
  }, [auth, passageId, personaId, progressDocId]);

  // Mark as complete
  const markComplete = useCallback(async () => {
    const lastStep = guidedStudy?.steps[totalSteps - 1];
    const lastSubStepIndex = lastStep ? getSubStepCount(lastStep) - 1 : 0;

    // Optimistic update (works for guests too)
    setCurrentStepIndex(totalSteps - 1);
    setCurrentSubStepIndex(lastSubStepIndex);
    setMaxReachedStepIndex(totalSteps - 1);
    setMaxReachedSubStepIndex(lastSubStepIndex);
    setIsCompleted(true);

    if (!auth || !passageId || !personaId) {
      return;
    }

    try {
      const activeUser = await ensureUser(auth);
      const db = getFirestore(getApp());
      const now = Timestamp.now();

      if (progressDocId) {
        // Update existing progress to mark as complete
        const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
        await updateDoc(progressRef, {
          currentStepIndex: totalSteps - 1,
          currentSubStepIndex: lastSubStepIndex,
          maxStepIndex: totalSteps - 1,
          maxSubStepIndex: lastSubStepIndex,
          totalSteps,
          completed: true,
          dateUpdated: now
        });
        const duration = Math.round((Date.now() - sessionStartTime) / 1000);
        Analytics.guidedStudyComplete(passageId, personaId, duration);
      } else {
        // Create new progress document marked as complete
        const progressRef = collection(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress);
        const newDoc = await addDoc(progressRef, {
          userId: activeUser.uid,
          passageId,
          personaId,
          currentStepIndex: totalSteps - 1,
          currentSubStepIndex: lastSubStepIndex,
          maxStepIndex: totalSteps - 1,
          maxSubStepIndex: lastSubStepIndex,
          totalSteps,
          completed: true,
          dateCreated: now,
          dateUpdated: now
        });
        setProgressDocId(newDoc.id);
        const duration = Math.round((Date.now() - sessionStartTime) / 1000);
        Analytics.guidedStudyComplete(passageId, personaId, duration);
      }
    } catch (error) {
      console.error('Failed to mark guided study as complete:', error);
    }
  }, [auth, passageId, personaId, totalSteps, guidedStudy, progressDocId]);

  // Save text response for a specific step/substep
  const saveTextResponse = useCallback(async (stepIndex: number, subStepIndex: number, text: string) => {
    // Update local state immediately (works for guests too)
    const newTextResponses = {
      ...textResponses,
      [stepIndex]: {
        ...(textResponses[stepIndex] || {}),
        [subStepIndex]: text
      }
    };
    setTextResponses(newTextResponses);

    if (!auth || !passageId || !personaId) return;

    // Save to Firestore in the background
    try {
      const activeUser = await ensureUser(auth);
      const db = getFirestore(getApp());
      const now = Timestamp.now();

      if (progressDocId) {
        // Update existing progress
        const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
        await updateDoc(progressRef, {
          textResponses: newTextResponses,
          dateUpdated: now
        });
      } else {
        // Create new progress document with text response
        const progressRef = collection(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress);
        const newDoc = await addDoc(progressRef, {
          userId: activeUser.uid,
          passageId,
          personaId,
          currentStepIndex,
          currentSubStepIndex,
          maxStepIndex: maxReachedStepIndex,
          maxSubStepIndex: maxReachedSubStepIndex,
          totalSteps,
          completed: false,
          textResponses: newTextResponses,
          dateCreated: now,
          dateUpdated: now
        });
        setProgressDocId(newDoc.id);
      }
    } catch (error) {
      console.error('Failed to save text response:', error);
    }
  }, [auth, passageId, personaId, textResponses, progressDocId, currentStepIndex, currentSubStepIndex, totalSteps, maxReachedStepIndex, maxReachedSubStepIndex]);

  // Mark a specific requirement as met
  const markRequirementMet = useCallback(async (stepIndex: number, subStepIndex: number) => {
    // Update local state immediately
    const newRequirements = {
      ...completedRequirements,
      [stepIndex]: {
        ...(completedRequirements[stepIndex] || {}),
        [subStepIndex]: true
      }
    };
    setCompletedRequirements(newRequirements);

    if (!auth || !passageId || !personaId) return;

    try {
      const activeUser = await ensureUser(auth);
      const db = getFirestore(getApp());
      const now = Timestamp.now();

      if (progressDocId) {
        const progressRef = doc(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress, progressDocId);
        await updateDoc(progressRef, {
          completedRequirements: newRequirements,
          dateUpdated: now
        });
      } else {
        const progressRef = collection(db, 'users', activeUser.uid, DBCollection.guidedStudyProgress);
        const newDoc = await addDoc(progressRef, {
          userId: activeUser.uid,
          passageId,
          personaId,
          currentStepIndex,
          currentSubStepIndex,
          maxStepIndex: maxReachedStepIndex,
          maxSubStepIndex: maxReachedSubStepIndex,
          totalSteps,
          completed: false,
          textResponses,
          completedRequirements: newRequirements,
          dateCreated: now,
          dateUpdated: now
        });
        setProgressDocId(newDoc.id);
      }
    } catch (error) {
      console.error('Failed to save requirement met:', error);
    }
  }, [auth, passageId, personaId, completedRequirements, progressDocId, currentStepIndex, currentSubStepIndex, maxReachedStepIndex, maxReachedSubStepIndex, totalSteps, textResponses]);

  // Check if a specific requirement is met
  const isRequirementMet = useCallback((stepIndex: number, subStepIndex: number): boolean => {
    return completedRequirements[stepIndex]?.[subStepIndex] || false;
  }, [completedRequirements]);

  // Get text response for a specific step/substep
  const getTextResponse = useCallback((stepIndex: number, subStepIndex: number): string => {
    return textResponses[stepIndex]?.[subStepIndex] || '';
  }, [textResponses]);

  const isFirstSubStep = currentSubStepIndex === 0;
  const isLastSubStep = currentSubStepIndex >= totalSubSteps - 1;
  const isFirstStep = currentStepIndex === 0 && isFirstSubStep;
  const isLastStep = currentStepIndex >= totalSteps - 1 && isLastSubStep;

  return {
    currentStepIndex,
    currentSubStepIndex,
    totalSubSteps,
    isLoading,
    isCompleted,
    maxReachedStepIndex,
    maxReachedSubStepIndex,
    goToNext,
    goToPrevious,
    jumpTo,
    resetProgress,
    markComplete,
    saveTextResponse,
    getTextResponse,
    markRequirementMet,
    isRequirementMet,
    isFirstStep,
    isLastStep
  };
}
